<template>
  <li class="activity-item">
    <div class="activity-item__header">
      <opportunity-activity-type-icon :type="activity.activity_type" />
      <p class="activity-item__title">
        <span class="activity-item__type">{{ typeLabel }}</span>
        <span class="activity-item__separator">-</span>
        <span
          class="activity-item__schedule"
          :class="`activity-item__schedule--${schedule.tone}`"
          :title="fullScheduledAt"
        >
          {{ $t(schedule.key, schedule.params) }}
        </span>
      </p>
      <div v-if="showActions" class="activity-item__actions">
        <woot-button
          type="button"
          color-scheme="success"
          size="small"
          icon="checkmark-circle"
          class="activity-item__complete"
          :is-disabled="isBusy"
          @click="$emit('complete', activity)"
        >
          {{ $t('OPPORTUNITY_DETAILS.ACTIVITIES.ACTIONS.COMPLETE') }}
        </woot-button>
        <woot-button
          type="button"
          color-scheme="secondary"
          size="small"
          icon="calendar-clock"
          class="activity-item__icon-action activity-item__reschedule"
          :title="$t('OPPORTUNITY_DETAILS.ACTIVITIES.ACTIONS.RESCHEDULE')"
          :aria-label="$t('OPPORTUNITY_DETAILS.ACTIVITIES.ACTIONS.RESCHEDULE')"
          :is-disabled="isBusy"
          @click="$emit('reschedule', activity)"
        />
        <woot-button
          type="button"
          color-scheme="secondary"
          size="small"
          icon="dismiss-circle"
          class="activity-item__icon-action activity-item__cancel"
          :title="$t('OPPORTUNITY_DETAILS.ACTIVITIES.ACTIONS.CANCEL')"
          :aria-label="$t('OPPORTUNITY_DETAILS.ACTIVITIES.ACTIONS.CANCEL')"
          :is-disabled="isBusy"
          @click="$emit('cancel', activity)"
        />
      </div>
    </div>
    <p v-if="activity.description" class="activity-item__description">
      {{ $t('OPPORTUNITY_DETAILS.ACTIVITIES.OBSERVATION') }}
      {{ activity.description }}
    </p>
    <p v-if="statusChangeLabel" class="activity-item__meta">
      {{ statusChangeLabel }}
    </p>
  </li>
</template>

<script>
import {
  activityScheduleInfo,
  activityTypeLabelKey,
  formatActivityDateTime,
} from 'dashboard/helper/opportunityActivityHelper';
import OpportunityActivityTypeIcon from './OpportunityActivityTypeIcon.vue';

const STATUS_CHANGE_KEYS = {
  completed: 'OPPORTUNITY_DETAILS.ACTIVITIES.COMPLETED_AT',
  canceled: 'OPPORTUNITY_DETAILS.ACTIVITIES.CANCELED_AT',
};

export default {
  name: 'OpportunityActivityItem',
  components: {
    OpportunityActivityTypeIcon,
  },
  props: {
    activity: {
      type: Object,
      required: true,
    },
    now: {
      type: Date,
      default: () => new Date(),
    },
    showActions: {
      type: Boolean,
      default: false,
    },
    isBusy: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    typeLabel() {
      return this.$t(activityTypeLabelKey(this.activity.activity_type));
    },
    schedule() {
      return activityScheduleInfo(this.activity, this.now);
    },
    fullScheduledAt() {
      return formatActivityDateTime(this.activity.scheduled_at);
    },
    statusChangeLabel() {
      const key = STATUS_CHANGE_KEYS[this.activity.status];
      if (!key || !this.activity.status_changed_at) {
        return '';
      }

      return this.$t(key, {
        date: formatActivityDateTime(this.activity.status_changed_at),
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.activity-item {
  background: var(--white);
  border: 1px solid var(--s-50);
  border-radius: var(--border-radius-medium);
  box-shadow: var(--shadow-small);
  list-style: none;
  margin-bottom: var(--space-normal);
  padding: var(--space-normal);
}

.activity-item__header {
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-small) var(--space-slab);
}

.activity-item__title {
  color: var(--s-900);
  flex: 1 1 14rem;
  font-size: var(--font-size-default);
  margin: 0;
  min-width: 0;
}

.activity-item__type,
.activity-item__separator {
  font-weight: var(--font-weight-bold);
}

.activity-item__schedule--overdue {
  color: var(--r-500);
  font-weight: var(--font-weight-bold);
}

.activity-item__schedule--today {
  color: var(--g-700);
  font-weight: var(--font-weight-bold);
}

.activity-item__schedule--future,
.activity-item__schedule--neutral {
  color: var(--s-600);
}

.activity-item__actions {
  align-items: center;
  display: flex;
  flex-shrink: 0;
  gap: var(--space-small);
  margin-left: auto;
}

.activity-item__actions .button {
  margin: 0;
}

.activity-item__icon-action {
  justify-content: center;
  width: 3.2rem;
}

.activity-item__description {
  color: var(--s-800);
  font-size: var(--font-size-small);
  margin: var(--space-normal) 0 0;
  overflow-wrap: anywhere;
}

.activity-item__meta {
  color: var(--s-500);
  font-size: var(--font-size-mini);
  margin: var(--space-smaller) 0 0;
}
</style>
